import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, ScrollText, Swords, KeyRound, Lightbulb, Loader2, MessageSquare } from "lucide-react";
import { API, SHOWCASE_ICONS } from "@/lib/gp";
import { Button } from "@/components/ui/button";

const SECTION_META = {
  strategy: { icon: Swords, label: "Strategy" },
  walkthrough: { icon: ScrollText, label: "Walkthrough" },
  cheats: { icon: KeyRound, label: "Cheat Codes" },
  tips: { icon: Lightbulb, label: "Pro Tips" },
};

export default function GameModule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Reset per request so a failed game never leaves "not found" on the next one,
    // and ignore a slow response that arrives after the visitor already moved on.
    let current = true;
    setLoading(true);
    setNotFound(false);
    setData(null);
    fetch(`${API}/games/${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => current && setData(d))
      .catch(() => current && setNotFound(true))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="animate-spin text-gp-blue" size={32} />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-28 text-center">
        <h1 className="font-unbounded font-black tracking-tighter text-4xl">Module not found</h1>
        <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-gp-blue">Back home</Button>
      </div>
    );
  }

  const { game, module: mod } = data;
  const Icon = SHOWCASE_ICONS[game.id] || SHOWCASE_ICONS.default;
  const accent = game.accent || "#19c37d";

  return (
    <div data-testid={`module-${game.id}`}>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-[340px] overflow-hidden">
          {game.image ? (
            <img src={game.image} alt={game.name} className="h-full w-full object-cover opacity-30" />
          ) : (
            <div className="h-full w-full" style={{ background: `linear-gradient(160deg, ${accent}33, transparent)` }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-gp-bg/40 via-gp-bg/80 to-gp-bg" />
        </div>
        <div className="relative mx-auto max-w-5xl px-5 md:px-8 pt-16 md:pt-24 pb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/50">
            <span className="grid place-items-center h-8 w-8 rounded-lg" style={{ background: `${accent}22`, color: accent }}>
              <Icon size={16} />
            </span>
            {game.genre} · {game.type === "multiplayer" ? "Multiplayer" : "Single-player"}
          </div>
          <h1 className="mt-4 font-unbounded font-black tracking-tighter text-4xl sm:text-5xl uppercase">{game.name}</h1>
          <p className="mt-4 text-white/75 text-lg max-w-2xl leading-relaxed">{mod.overview}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              data-testid="module-ask-yorick"
              onClick={() => navigate(`/chat?game=${encodeURIComponent(game.id)}`)}
              className="rounded-full bg-gp-blue hover:bg-gp-blue/90 text-white font-bold px-6 shadow-[0_0_22px_rgba(25,195,125,0.4)]"
            >
              <MessageSquare size={17} className="mr-2" /> Ask Yorick about {game.name}
            </Button>
            {mod.fairplay_note && (
              <span className="flex items-center gap-2 text-sm text-gp-green bg-gp-green/10 px-4 py-2 rounded-full ring-1 ring-gp-green/25">
                <ShieldCheck size={15} /> Fair-play only
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-5xl px-5 md:px-8 pb-16 space-y-6">
        {mod.fairplay_note && (
          <div className="glass rounded-2xl p-5 border-l-2 border-gp-green/60 text-white/70 text-sm leading-relaxed" data-testid="module-fairplay">
            {mod.fairplay_note}
          </div>
        )}

        {mod.sections.map((sec, i) => {
          const meta = SECTION_META[sec.type] || SECTION_META.tips;
          const SIcon = meta.icon;
          const isCheat = sec.type === "cheats";
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="glass rounded-2xl p-6 md:p-8"
              data-testid={`module-section-${sec.type}`}
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: `${accent}1f`, color: accent }}>
                  <SIcon size={18} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/40">{meta.label}</p>
                  <h2 className="font-unbounded font-bold text-xl">{sec.title}</h2>
                </div>
              </div>

              {isCheat ? (
                <div className="space-y-2">
                  {sec.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-3 rounded-xl bg-black/25 px-4 py-3">
                      <KeyRound size={15} className="text-gp-gold shrink-0" />
                      <span className="text-white/85">{it}</span>
                    </div>
                  ))}
                  {sec.note && <p className="text-xs text-white/45 mt-3 leading-relaxed">{sec.note}</p>}
                </div>
              ) : (
                <ol className="space-y-3">
                  {sec.items.map((it, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="grid place-items-center h-6 w-6 rounded-md text-xs font-bold shrink-0 mt-0.5" style={{ background: `${accent}22`, color: accent }}>
                        {sec.type === "walkthrough" ? idx + 1 : "◆"}
                      </span>
                      <span className="text-white/85 leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ol>
              )}
            </motion.div>
          );
        })}

        <div className="glass rounded-2xl p-8 text-center">
          <h3 className="font-unbounded font-bold text-2xl">Want a plan for your exact run?</h3>
          <p className="text-white/60 mt-2">Yorick tailors strategy to your situation in seconds.</p>
          <Button onClick={() => navigate(`/chat?game=${encodeURIComponent(game.id)}`)} className="mt-6 rounded-full bg-gp-blue hover:bg-gp-blue/90 text-white font-bold px-7">
            Open Yorick chat <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
